<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format as formatDate, parseISO } from 'date-fns';
import BarChart from 'shared/components/charts/BarChart.vue';
import DoughnutChart from 'shared/components/charts/DoughnutChart.vue';
import LineChart from 'shared/components/charts/DashboardLineChart.vue';
import { formatPercent, formatWidgetValue } from './widgetFormatters';
import {
  OTHERS_COLOR,
  barDataset,
  barOptions,
  doughnutDataset,
  doughnutOptions,
  lineDataset,
  lineOptions,
  paletteFor,
} from '../../charts/chartDesign';
import { useChartTheme } from '../../charts/useChartTheme';

const props = defineProps({
  visualization: { type: String, required: true },
  data: { type: Object, required: true },
});

const { t } = useI18n();
const theme = useChartTheme();

const slices = computed(() => {
  const items = props.data.items.map((item, index) => ({
    ...item,
    color: paletteFor(index),
  }));
  if (props.data.others) {
    items.push({
      key: 'others',
      label: t('COMMERCIAL_WIDGETS.TABLE.OTHERS'),
      ...props.data.others,
      color: OTHERS_COLOR,
    });
  }
  return items;
});

const topSlice = computed(() => slices.value[0]);

const hasData = computed(() =>
  props.visualization === 'line'
    ? props.data.series.length > 0
    : slices.value.length > 0
);

const formatTick = value => formatWidgetValue(value, props.data.format);

const collection = computed(() => {
  if (props.visualization === 'line') {
    return {
      labels: props.data.series.map(row =>
        formatDate(parseISO(row.date), 'dd/MM')
      ),
      datasets: [
        lineDataset({
          data: props.data.series.map(row => row.value),
          theme,
        }),
      ],
    };
  }
  const labels = slices.value.map(item => item.label);
  const data = slices.value.map(item => item.value);
  if (props.visualization === 'doughnut') {
    return {
      labels,
      datasets: [
        doughnutDataset({
          data,
          colors: slices.value.map(item => item.color.hex),
        }),
      ],
    };
  }
  return {
    labels,
    datasets: [
      barDataset({
        data,
        isMuted: index =>
          !!props.data.others && index === slices.value.length - 1,
      }),
    ],
  };
});

const lineChartOptions = computed(() =>
  lineOptions({ theme: theme.value, formatTick })
);
const barChartOptions = computed(() =>
  barOptions({ theme: theme.value, formatTick })
);
const doughnutChartOptions = computed(() => doughnutOptions({ formatTick }));
</script>

<template>
  <div class="relative flex-1 min-h-0 overflow-hidden min-w-0">
    <p v-if="!hasData" class="text-sm text-n-slate-10">
      {{ t('COMMERCIAL_WIDGETS.EMPTY_DATA') }}
    </p>
    <div
      v-else-if="visualization === 'doughnut'"
      class="flex items-center h-full gap-3 min-w-0"
    >
      <div class="relative flex-1 h-full min-w-0">
        <DoughnutChart
          :collection="collection"
          :chart-options="doughnutChartOptions"
        />
        <div
          class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
        >
          <span class="text-lg font-semibold leading-none text-n-slate-12">
            {{ formatPercent(topSlice.percent) }}
          </span>
          <span
            class="max-w-[4.5rem] mt-0.5 text-[10px] truncate text-n-slate-11"
          >
            {{ topSlice.label }}
          </span>
        </div>
      </div>
      <ul
        class="flex flex-col flex-1 max-h-full gap-1.5 p-0 m-0 overflow-y-auto list-none min-w-0"
      >
        <li
          v-for="slice in slices"
          :key="slice.key"
          data-legend-item
          class="flex items-center gap-1.5 text-xs min-w-0"
        >
          <span class="rounded-full size-2 shrink-0" :class="slice.color.dot" />
          <span class="truncate text-n-slate-11" :title="slice.label">
            {{ slice.label }}
          </span>
          <span class="ml-auto font-medium tabular-nums text-n-slate-12">
            {{ formatPercent(slice.percent) }}
          </span>
        </li>
      </ul>
    </div>
    <LineChart
      v-else-if="visualization === 'line'"
      :collection="collection"
      :chart-options="lineChartOptions"
    />
    <BarChart
      v-else
      :collection="collection"
      :chart-options="barChartOptions"
    />
  </div>
</template>
